import { RouteObject, createBrowserRouter, Navigate } from 'react-router-dom';
import { LoginForm } from './components/auth/LoginForm';
import { UnauthorizedPage } from './components/auth/UnauthorizedPage';
import { AuthTroubleshooter } from './components/auth/AuthTroubleshooter';
import { Dashboard } from './components/dashboard/Dashboard';
import { AdminRouteOutlet } from './components/auth/AdminRouteOutlet';
import { AdminDashboard } from './components/dashboard/AdminDashboard';
import { AdminMilestonesPage } from './components/admin/AdminMilestonesPage';
import { AdminReportsPage } from './components/admin/AdminReportsPage';
import { TermsOfService } from './components/legal/TermsOfService';
import { PrivacyPolicy } from './components/legal/PrivacyPolicy';
import { ReportSubmission } from './components/reports/ReportSubmission';
import { ReportsPage } from './components/reports/ReportsPage';
import { MilestonesPage } from './components/milestones/MilestonesPage';
import { InitiativeLeadReportView } from './components/reports/InitiativeLeadReportView';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { DashboardErrorBoundary } from './components/error/DashboardErrorBoundary';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AzureRoleProtectedRoute } from './components/auth/AzureRoleProtectedRoute';
import { Outlet } from 'react-router-dom';
import { NotFoundErrorBoundary } from './components/error/NotFoundErrorBoundary';
import { useAuthStore } from './stores/authStore';

// Layout component that includes Navbar and Footer
const Layout = () => (
  <>
    <Navbar />
    <Outlet />
    <Footer />
  </>
);

// Protected layout that wraps protected routes
const ProtectedLayout = () => {
  const { isAuthenticated } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return (
    <ProtectedRoute>
      <Layout />
    </ProtectedRoute>
  );
};

// Login route with auth check
const LoginRoute = () => {
  const { isAuthenticated } = useAuthStore();
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return (
    <>
      <Navbar />
      <LoginForm />
      <Footer />
    </>
  );
};

export const routes: RouteObject[] = [
  {
    path: '/login',
    element: <LoginRoute />
  },
  {
    path: '/unauthorized',
    element: <UnauthorizedPage />
  },
  {
    path: '/auth-help',
    element: <AuthTroubleshooter />
  },
  {
    path: '/',
    element: <ProtectedLayout />,
    children: [
      {
        index: true,
        element: (
          <DashboardErrorBoundary>
            <Dashboard />
          </DashboardErrorBoundary>
        )
      },
      {
        path: 'admin',
        element: <AdminRouteOutlet />,
        children: [
          {
            index: true,
            element: <Navigate to="dashboard" replace />
          },
          {
            path: 'dashboard',
            element: <AdminDashboard />
          },
          {
            path: 'reports/:reportId',
            element: <ReportSubmission />
          },
          {
            path: 'reports',
            element: <AdminReportsPage />
          },
          {
            path: 'milestones',
            element: <AdminMilestonesPage />
          },
          {
            path: 'report-review/:reportId/:subawardee',
            element: <InitiativeLeadReportView />
          }
        ]
      },
      {
        path: 'admin-direct',
        element: (
          <AzureRoleProtectedRoute requiredRole="TIL.PMP.ADMIN">
            <Navigate to="/admin/dashboard" replace />
          </AzureRoleProtectedRoute>
        )
      },
      {
        path: 'terms',
        element: <TermsOfService />
      },
      {
        path: 'privacy',
        element: <PrivacyPolicy />
      },
      {
        path: 'reports/:reportId',
        element: <ReportSubmission />
      },
      {
        path: 'reports',
        element: <ReportsPage />
      },
      {
        path: 'milestones',
        element: <MilestonesPage />
      },
      // Add catch-all route as the last child
      {
        path: '*',
        element: <NotFoundErrorBoundary />
      }
    ]
  }
];

// Create and export the router instance
export const router = createBrowserRouter(routes); 